import React, { useState, useEffect, useCallback } from 'react';
import {
  getVolunteerSessions,
  markAttendance,
  addSessionNote,
  getProgrammes,
  getSessionVolunteerOpportunities,
  getSessionVolunteerSignup,
  signUpAsSessionVolunteer,
  withdrawSessionVolunteerSignup,
} from '../../../api/programmesApi';

/* ─── Helpers ───────────────────────────────────────────────────── */
function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' });
}
function isUpcoming(session) {
  if (!session.date) return false;
  const d = session.date.toDate ? session.date.toDate() : new Date(session.date);
  return d >= new Date();
}
function isPast(session) { return !isUpcoming(session); }

/* ─── Tab: My Sessions ──────────────────────────────────────────── */
function MySessions({ orgId, volunteerId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getVolunteerSessions(orgId, volunteerId)
      .then(data => setSessions(data))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [orgId, volunteerId]);

  const upcoming = sessions.filter(isUpcoming);
  const now = new Date();
  const thisMonth = sessions.filter(s => {
    const d = s.date?.toDate ? s.date.toDate() : new Date(s.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const nextSession = upcoming[0];

  if (loading) return (
    <div className="bb-loading-row">
      <div className="bb-loading-spinner" />
      <span>Loading sessions…</span>
    </div>
  );

  return (
    <div className="bb-vol-section">
      <div className="bb-stats-row" style={{ marginBottom: 20 }}>
        <div className="bb-stat-card">
          <div className="bb-stat-value">{thisMonth.length}</div>
          <div className="bb-stat-label">Sessions This Month</div>
        </div>
        <div className="bb-stat-card">
          <div className="bb-stat-value">{nextSession ? formatDate(nextSession.date) : '—'}</div>
          <div className="bb-stat-label">Next Session</div>
        </div>
        <div className="bb-stat-card">
          <div className="bb-stat-value">{upcoming.length}</div>
          <div className="bb-stat-label">Upcoming Sessions</div>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="bb-empty-state">No sessions assigned to you.</div>
      ) : (
        <table className="bb-table">
          <thead>
            <tr>
              <th>Programme</th>
              <th>Date</th>
              <th>Time</th>
              <th>Location</th>
              <th>Enrolled</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(s => (
              <tr key={s.id}>
                <td style={{ fontWeight: 500 }}>{s.programmeName || '—'}</td>
                <td>{formatDate(s.date)}</td>
                <td>{formatTime(s.date)}</td>
                <td>{s.location || '—'}</td>
                <td>{(s.enrolledMemberIds || []).length}</td>
                <td>
                  <span className={`bb-badge${isUpcoming(s) ? ' bb-badge--green' : ' bb-badge--gray'}`}>
                    {isUpcoming(s) ? 'Upcoming' : 'Past'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── Tab: Attendance ───────────────────────────────────────────── */
function Attendance({ orgId, volunteerId }) {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [session, setSession] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getVolunteerSessions(orgId, volunteerId)
      .then(data => { setSessions(data.filter(isPast)); })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [orgId, volunteerId]);

  useEffect(() => {
    if (!selectedId) { setSession(null); return; }
    const s = sessions.find(s => s.id === selectedId);
    setSession(s || null);
    setAttendance(s?.attendance || {});
    setSaved(false);
  }, [selectedId, sessions]);

  const toggleAttendance = (memberId, present) => {
    setAttendance(a => ({ ...a, [memberId]: present ? 'present' : 'absent' }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!session) return;
    setSaving(true);
    try {
      for (const memberId of (session.enrolledMemberIds || [])) {
        const present = attendance[memberId] === 'present';
        await markAttendance(orgId, session.id, memberId, present);
      }
      setSaved(true);
    } catch { /* handled silently */ }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="bb-loading-row">
      <div className="bb-loading-spinner" />
      <span>Loading sessions…</span>
    </div>
  );

  return (
    <div className="bb-vol-section">
      <div className="bb-form-group" style={{ maxWidth: 320 }}>
        <label>Select Past Session</label>
        <select className="bb-select" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
          <option value="">— choose a session —</option>
          {sessions.map(s => (
            <option key={s.id} value={s.id}>
              {s.programmeName} — {formatDate(s.date)}
            </option>
          ))}
        </select>
      </div>

      {session && (
        <>
          <h4 style={{ marginBottom: 8, color: '#1F2937' }}>
            {session.programmeName} — {formatDate(session.date)}
          </h4>
          {(session.enrolledMemberIds || []).length === 0 ? (
            <div className="bb-empty-state">No enrolled members for this session.</div>
          ) : (
            <table className="bb-table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Present</th>
                  <th>Absent</th>
                </tr>
              </thead>
              <tbody>
                {(session.enrolledMemberIds || []).map(mId => (
                  <tr key={mId}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{mId}</td>
                    <td>
                      <button
                        className={attendance[mId] === 'present' ? 'bb-btn-sm' : 'bb-btn-outline-sm'}
                        style={attendance[mId] === 'present' ? { background: '#4338CA', color: '#fff' } : {}}
                        onClick={() => toggleAttendance(mId, true)}
                      >
                        Present
                      </button>
                    </td>
                    <td>
                      <button
                        className={attendance[mId] === 'absent' ? 'bb-btn-danger-sm' : 'bb-btn-outline-sm'}
                        onClick={() => toggleAttendance(mId, false)}
                      >
                        Absent
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {saved && <div className="bb-alert bb-alert--info" style={{ marginBottom: 8 }}>Attendance saved successfully.</div>}
          <button
            className="bb-btn-primary"
            style={{ background: '#4338CA' }}
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save Attendance'}
          </button>
        </>
      )}
    </div>
  );
}

/* ─── Tab: Session Notes ────────────────────────────────────────── */
function SessionNotes({ orgId, volunteerId, volunteerName }) {
  const [sessions, setSessions] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [session, setSession] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  // referral sub-section
  const [refMember, setRefMember] = useState('');
  const [refProgramme, setRefProgramme] = useState('');
  const [refSaved, setRefSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      getVolunteerSessions(orgId, volunteerId),
      getProgrammes(orgId),
    ]).then(([sess, progs]) => {
      setSessions(sess);
      setProgrammes(progs);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [orgId, volunteerId]);

  useEffect(() => {
    if (!selectedId) { setSession(null); setNoteText(''); return; }
    const s = sessions.find(s => s.id === selectedId);
    setSession(s || null);
    setNoteText('');
    setSaved(false);
  }, [selectedId, sessions]);

  const handleSaveNote = async () => {
    if (!session || !noteText.trim()) return;
    setSaving(true);
    try {
      await addSessionNote(orgId, session.id, noteText.trim(), volunteerId, volunteerName);
      setSaved(true);
      setNoteText('');
      // refresh session
      const updated = sessions.find(s => s.id === selectedId);
      if (updated) setSession({ ...updated });
    } catch { /* handled */ }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="bb-loading-row">
      <div className="bb-loading-spinner" />
      <span>Loading sessions…</span>
    </div>
  );

  return (
    <div className="bb-vol-section">
      <div className="bb-form-group" style={{ maxWidth: 340 }}>
        <label>Select Session</label>
        <select className="bb-select" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
          <option value="">— choose a session —</option>
          {sessions.map(s => (
            <option key={s.id} value={s.id}>
              {s.programmeName} — {formatDate(s.date)}
            </option>
          ))}
        </select>
      </div>

      {session && (
        <>
          <div className="bb-alert bb-alert--info" style={{ marginBottom: 12 }}>
            These notes are visible to admin only and will never be shared with members.
          </div>

          {/* Previous notes */}
          {(session.sessionNotes || []).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Previous Notes</h4>
              {session.sessionNotes.map((n, i) => (
                <div key={i} className="bb-card" style={{ padding: '10px 14px', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {n.authorName} — {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                  </div>
                  <div style={{ fontSize: 13, color: '#1F2937' }}>{n.text}</div>
                </div>
              ))}
            </div>
          )}

          <div className="bb-form-group">
            <label>New Note</label>
            <textarea
              className="bb-textarea"
              rows={4}
              placeholder="Add a session note…"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
            />
          </div>
          {saved && <div className="bb-alert bb-alert--info" style={{ marginBottom: 8 }}>Note saved.</div>}
          <button
            className="bb-btn-primary"
            style={{ background: '#4338CA', marginBottom: 24 }}
            disabled={saving || !noteText.trim()}
            onClick={handleSaveNote}
          >
            {saving ? 'Saving…' : 'Add Note'}
          </button>

          {/* Member Referrals sub-section */}
          <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 20 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
              Recommend Member for Programme
            </h4>
            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Member Name</label>
                <input
                  className="bb-input"
                  placeholder="Member name or ID"
                  value={refMember}
                  onChange={e => setRefMember(e.target.value)}
                />
              </div>
              <div className="bb-form-group">
                <label>Programme</label>
                <select className="bb-select" value={refProgramme} onChange={e => setRefProgramme(e.target.value)}>
                  <option value="">— select programme —</option>
                  {programmes.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {refSaved && <div className="bb-alert bb-alert--info" style={{ marginBottom: 8 }}>Recommendation noted. Admin has been notified.</div>}
            <button
              className="bb-btn-outline-sm"
              style={{ borderColor: '#4338CA', color: '#4338CA' }}
              disabled={!refMember || !refProgramme}
              onClick={() => { setRefSaved(true); setRefMember(''); setRefProgramme(''); }}
            >
              Recommend for Programme
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Tab: Resource Library ─────────────────────────────────────── */
const RESOURCES = [
  { title: 'RAINBOWS Facilitator Guide', description: 'Complete facilitator guide for the RAINBOWS grief support programme. Covers session structure, group dynamics, and facilitator self-care.' },
  { title: 'Upper Room Session Templates', description: 'Ready-to-use session templates for Upper Room grief support gatherings, including opening prayers, reflective activities, and closing exercises.' },
  { title: 'Integrated Therapy Resources', description: 'Resource pack for integrated therapy sessions, including art therapy prompts, narrative therapy frameworks, and somatic exercises.' },
  { title: 'Care & Connect Facilitation Guide', description: 'Step-by-step facilitation guide for Care & Connect peer support groups, including icebreakers, discussion prompts, and crisis response protocols.' },
];

function ResourceLibrary() {
  return (
    <div className="bb-vol-section">
      <div className="bb-alert bb-alert--info" style={{ marginBottom: 16 }}>
        Resources uploaded by admin will appear here. Download buttons will be enabled once files are available.
      </div>
      {RESOURCES.map((r, i) => (
        <div key={i} className="bb-card" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>📄 {r.title}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{r.description}</div>
          </div>
          <button
            className="bb-btn-outline-sm"
            disabled
            style={{ opacity: 0.45, flexShrink: 0, marginLeft: 16 }}
            title="Resources uploaded by admin will appear here"
          >
            Download
          </button>
        </div>
      ))}
    </div>
  );
}

/* ─── Tab: Session Volunteer Opportunities ──────────────────────── */
function SessionVolunteerOpportunities({ orgId, volunteerId, volunteerName }) {
  const [sessions, setSessions]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [signupStatus, setSignupStatus] = useState({});
  const [actionMsg, setActionMsg]       = useState('');

  const showMsg = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await getSessionVolunteerOpportunities(orgId);
      setSessions(data);
      const statuses = {};
      await Promise.all(data.map(async (s) => {
        try {
          const signup = await getSessionVolunteerSignup(orgId, s.id, volunteerId);
          statuses[s.id] = signup ? 'signed_up' : null;
        } catch {
          statuses[s.id] = null;
        }
      }));
      setSignupStatus(statuses);
    } catch {
      setError('Failed to load session volunteer opportunities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgId, volunteerId]);

  useEffect(() => { load(); }, [load]);

  const handleSignUp = async (s) => {
    setSignupStatus(p => ({ ...p, [s.id]: 'loading' }));
    try {
      await signUpAsSessionVolunteer(orgId, s.id, volunteerId, volunteerName);
      setSignupStatus(p => ({ ...p, [s.id]: 'signed_up' }));
      setSessions(p => p.map(x => x.id === s.id ? { ...x, volunteer_signup_count: (x.volunteer_signup_count || 0) + 1 } : x));
      showMsg(`You've signed up to volunteer for "${s.programmeName || 'this session'}"!`);
    } catch (e) {
      setSignupStatus(p => ({ ...p, [s.id]: null }));
      setError(e.message || 'Sign-up failed. Please try again.');
    }
  };

  const handleWithdraw = async (s) => {
    if (!window.confirm(`Withdraw your volunteer sign-up for this session?`)) return;
    setSignupStatus(p => ({ ...p, [s.id]: 'loading' }));
    try {
      await withdrawSessionVolunteerSignup(orgId, s.id, volunteerId);
      setSignupStatus(p => ({ ...p, [s.id]: null }));
      setSessions(p => p.map(x => x.id === s.id ? { ...x, volunteer_signup_count: Math.max((x.volunteer_signup_count || 1) - 1, 0) } : x));
      showMsg(`You've withdrawn your volunteer sign-up.`);
    } catch (e) {
      setSignupStatus(p => ({ ...p, [s.id]: 'signed_up' }));
      setError(e.message || 'Withdrawal failed. Please try again.');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="bb-loading-spinner" /></div>;

  return (
    <div className="bb-vol-section">
      {error    && <div className="bb-alert bb-alert--error"   style={{ marginBottom: 12 }}>{error}</div>}
      {actionMsg && <div className="bb-alert bb-alert--success" style={{ marginBottom: 12 }}>{actionMsg}</div>}

      {sessions.length === 0 ? (
        <div className="bb-empty-state">No sessions currently require volunteers. Check back soon!</div>
      ) : (
        sessions.map(s => {
          const status   = signupStatus[s.id];
          const isFull   = (s.volunteer_signup_count || 0) >= (s.volunteers_needed || 0);
          const signedUp = status === 'signed_up';
          const busy     = status === 'loading';
          const dateStr  = s.date ? (s.date.toDate ? s.date.toDate() : new Date(s.date)).toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

          return (
            <div key={s.id} className="bb-vol-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ background: '#4338CA', height: 6 }} />
              <div style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Session</span>
                      {signedUp && <span className="bb-badge bb-badge--green">You're Signed Up</span>}
                      {isFull && !signedUp && <span className="bb-badge bb-badge--grey">Full</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 2 }}>
                      {s.programmeName || 'Session'}
                    </div>
                    <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>
                      📅 {dateStr}{s.time ? ` at ${s.time}` : ''}
                    </div>
                    {s.location && (
                      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>📍 {s.location}</div>
                    )}
                    {s.notes && (
                      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>{s.notes}</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <span style={{ fontSize: 12, color: '#6B7280' }}>Volunteers needed:</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: isFull ? '#059669' : '#92400E' }}>
                        {s.volunteer_signup_count || 0} / {s.volunteers_needed || 0}
                      </span>
                      <div style={{ flex: 1, maxWidth: 100, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3,
                          background: isFull ? '#059669' : '#4338CA',
                          width: `${Math.min(((s.volunteer_signup_count || 0) / (s.volunteers_needed || 1)) * 100, 100)}%`,
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                    {signedUp ? (
                      <button className="bb-btn-danger-sm" style={{ padding: '6px 14px' }} onClick={() => handleWithdraw(s)} disabled={busy}>
                        {busy ? '…' : 'Withdraw'}
                      </button>
                    ) : (
                      <button
                        className="bb-btn-primary"
                        style={{ padding: '6px 14px', fontSize: 13, opacity: isFull ? 0.5 : 1 }}
                        onClick={() => handleSignUp(s)}
                        disabled={busy || isFull}
                      >
                        {busy ? 'Signing up…' : isFull ? 'No Spots Left' : 'Volunteer for this Session'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ─── Root ──────────────────────────────────────────────────────── */
function OrgProgrammesVolunteer({ orgId, volunteerId, volunteerName, activeTab }) {
  return (
    <div className="bb-vol-tool">
      <div className="bb-vol-tool-header">
        <h2>Organisation Programmes</h2>
        {activeTab === 'Volunteer Opportunities' && (
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>
            Sessions that need volunteers — sign up to help make them a success.
          </p>
        )}
      </div>
      {activeTab === 'My Sessions'          && <MySessions orgId={orgId} volunteerId={volunteerId} />}
      {activeTab === 'Attendance'           && <Attendance orgId={orgId} volunteerId={volunteerId} />}
      {activeTab === 'Session Notes'        && <SessionNotes orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />}
      {activeTab === 'Resource Library'     && <ResourceLibrary />}
      {activeTab === 'Volunteer Opportunities' && <SessionVolunteerOpportunities orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />}
    </div>
  );
}

export default OrgProgrammesVolunteer;
